import { motion } from "framer-motion";
import { BedDouble, Bath, Tv, Coffee, Wind, Shirt, Sparkles, Briefcase, Wifi, Car, TreePine, Wine, Flame, Puzzle, Clapperboard, Croissant, Plane, Baby, MapPinned, PawPrint } from "lucide-react";
import { useLang } from "@/i18n";

const roomIcons = [Bath, BedDouble, Tv, Coffee, Coffee, Shirt, Wind, Sparkles, Briefcase, Sparkles];
const propIcons = [Wifi, Car, TreePine, Wine, Flame, Flame, Puzzle, Clapperboard, Croissant];
const servIcons = [Croissant, Plane, Baby, MapPinned];

export default function Amenities() {
  const { t } = useLang();
  const groups = [
    { title: t("am.g1"), items: t("am.room"), icons: roomIcons, span: "lg:col-span-2", testid: "amenities-room" },
    { title: t("am.g2"), items: t("am.property"), icons: propIcons, span: "lg:col-span-2", testid: "amenities-property" },
    { title: t("am.g3"), items: t("am.services"), icons: servIcons, span: "lg:col-span-1", testid: "amenities-services" },
  ];

  return (
    <section className="py-24 md:py-32" data-testid="amenities-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("am.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-2xl"
        >
          {t("am.title")}
        </motion.h2>

        <div className="mt-14 grid lg:grid-cols-5 gap-4">
          {groups.map((g, gi) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: gi * 0.12 }}
              className={`${g.span} bg-cream-light border border-warm-grey/40 rounded-3xl p-8 md:p-10`}
              data-testid={g.testid}
            >
              <h3 className="font-serif text-2xl md:text-3xl text-main">{g.title}</h3>
              <ul className={`mt-6 grid gap-x-6 gap-y-3.5 ${g.items.length > 6 ? "sm:grid-cols-2" : ""}`}>
                {g.items.map((item, i) => {
                  const Icon = g.icons[i] || Sparkles;
                  return (
                    <li key={item} className="flex items-center gap-3 text-sm text-brown/90 font-light">
                      <Icon size={16} className="text-terracotta shrink-0" />
                      {item}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.36 }}
            className="lg:col-span-1 bg-green-dark text-cream-light rounded-3xl p-8 md:p-10 flex flex-col justify-between"
            data-testid="amenities-pet"
          >
            <PawPrint size={32} className="text-sage" />
            <p className="mt-6 font-serif text-xl leading-snug">{t("am.pet")}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
